/**
 * What the Opposition screen already knows about a matter's first run.
 *
 * An opposition is opened on one screen and drafted from another. The notice's
 * date and the grounds it pleads are typed on the first, and the run that
 * answers them is started from the Case screen, which has no run yet to carry
 * them over from (`RerunCard` carries what the *last run* held). Without this
 * the date and grounds were typed once and then lost, the first run went out
 * with neither, and the reconciler blocked it for want of a report date.
 *
 * A convenience, not a record. It prefills two fields that stay visible and
 * editable on the Case screen, so a missing or stale entry costs a person a
 * few keystrokes and never changes what is sent without their seeing it. It
 * lives in `sessionStorage` for the same reasons as `lastMatter.ts` (it dies
 * with the tab, and every access is guarded because storage can be missing or
 * can throw), and it is dropped the moment a run starts or the person signs out.
 */

const STORAGE_KEY = 'tmverse.runDefaults'

export interface RunDefaults {
  /** The date on the notice (or report), `YYYY-MM-DD`, or `''` when none was given. */
  reportDate: string
  grounds: string[]
}

type Store = Record<string, RunDefaults>

function readStore(): Store {
  try {
    const raw = window.sessionStorage.getItem(STORAGE_KEY)
    if (!raw) return {}
    const parsed: unknown = JSON.parse(raw)
    if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) return {}
    const store: Store = {}
    for (const [matterId, value] of Object.entries(parsed as Record<string, unknown>)) {
      if (typeof value !== 'object' || value === null) continue
      const { reportDate, grounds } = value as Record<string, unknown>
      if (typeof reportDate !== 'string') continue
      if (!Array.isArray(grounds) || !grounds.every((g) => typeof g === 'string')) continue
      store[matterId] = { reportDate, grounds: grounds as string[] }
    }
    return store
  } catch {
    return {}
  }
}

function writeStore(store: Store): void {
  try {
    if (Object.keys(store).length === 0) window.sessionStorage.removeItem(STORAGE_KEY)
    else window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(store))
  } catch {
    // A convenience must never take the screen down with it.
  }
}

/** Remember what to prefill the first run of `matterId` with. */
export function rememberRunDefaults(matterId: string, defaults: RunDefaults): void {
  if (!matterId) return
  const store = readStore()
  store[matterId] = { reportDate: defaults.reportDate, grounds: [...defaults.grounds] }
  writeStore(store)
}

/** What was remembered for `matterId`, or `null`. */
export function recallRunDefaults(matterId: string): RunDefaults | null {
  return readStore()[matterId] ?? null
}

/** Drop one matter's entry (a run started, so the run itself now carries it). */
export function forgetRunDefaults(matterId: string): void {
  const store = readStore()
  if (matterId in store) {
    delete store[matterId]
    writeStore(store)
  }
}

/** Drop everything (sign-out). */
export function clearRunDefaults(): void {
  writeStore({})
}
